import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { Auth } from '../../../core/services/auth.service';

type AuthView = 'login' | 'register' | 'forgot-email' | 'verify-code' | 'reset-password';

@Component({
  selector: 'app-auth-modal',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, ToastModule],
  providers: [MessageService],
  templateUrl: './auth-modal.html',
  styleUrl: './auth-modal.css',
})
export class AuthModal implements OnInit {
  @Input() inviteToken: string | null = null;
  @Output() closeModal = new EventEmitter<void>();
  /** Emite cuando el registro con invitación terminó bien (para quitar ?invite= de la URL). */
  @Output() inviteConsumed = new EventEmitter<void>();

  currentView: AuthView = 'login';
  isLoading: boolean = false;
  inviteLocked = false;
  inviteHint = '';
  private inviteApplied = false;

  loginForm: FormGroup;
  registerForm: FormGroup;
  forgotEmailForm: FormGroup;
  verifyCodeForm: FormGroup;
  resetPasswordForm: FormGroup;

  emailForRecovery: string = '';
  codeForRecovery: string = '';

  constructor(private fb: FormBuilder, private auth: Auth, private messageService: MessageService) {
    this.loginForm = this.fb.group({
      username: ['', Validators.required],
      password: ['', Validators.required]
    });

    this.registerForm = this.fb.group({
      fullname: ['', Validators.required],
      username: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required, Validators.minLength(6)]],
      telephone: ['', Validators.required],
      birthDate: ['', Validators.required],
      inviteToken: ['']
    });

    this.forgotEmailForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]]
    });

    this.verifyCodeForm = this.fb.group({
      code: ['', [Validators.required, Validators.minLength(6), Validators.maxLength(6)]]
    });

    this.resetPasswordForm = this.fb.group({
      newPassword: ['', [Validators.required, Validators.minLength(6)]],
      confirmPassword: ['', Validators.required]
    });
  }

  ngOnInit() {
    if (this.inviteToken && !this.inviteApplied) {
      this.applyInvite(this.inviteToken);
    }
  }

  private applyInvite(token: string) {
    this.inviteApplied = true;
    this.currentView = 'register';
    this.isLoading = true;
    this.auth.getInvitePreview(token).subscribe({
      next: (preview) => {
        this.isLoading = false;
        if (!preview.valid) {
          this.messageService.add({
            severity: 'warn',
            summary: 'Invitación',
            detail: preview.message || 'Invitación no válida.'
          });
          // Token inválido/usado: quitar de la URL y mostrar login normal
          this.inviteConsumed.emit();
          this.inviteLocked = false;
          this.setView('login');
          return;
        }
        this.inviteLocked = true;
        this.inviteHint = 'Completa usuario y contraseña para activar tu cuenta existente.';
        this.registerForm.patchValue({
          fullname: preview.nombreCliente || '',
          email: preview.email || '',
          telephone: preview.celular || '',
          inviteToken: token
        });
        this.registerForm.get('email')?.disable({ emitEvent: false });
        this.registerForm.get('telephone')?.disable({ emitEvent: false });
        if (preview.nombreCliente) {
          this.registerForm.get('fullname')?.disable({ emitEvent: false });
        }
      },
      error: () => {
        this.isLoading = false;
        this.inviteConsumed.emit();
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'No se pudo validar la invitación.'
        });
        this.setView('login');
      }
    });
  }

  setView(view: AuthView) {
    this.currentView = view;
    if (view === 'login') this.loginForm.reset();
    if (view === 'register' && !this.inviteLocked) {
      this.registerForm.reset();
      this.registerForm.get('email')?.enable({ emitEvent: false });
      this.registerForm.get('telephone')?.enable({ emitEvent: false });
      this.registerForm.get('fullname')?.enable({ emitEvent: false });
    }
    if (view === 'forgot-email') this.forgotEmailForm.reset();
  }

  onClose() {
    this.closeModal.emit();
  }

  onLogin() {
    if (this.loginForm.invalid) {
      this.loginForm.markAllAsTouched();
      return;
    }

    this.isLoading = true;
    const { username, password } = this.loginForm.value;

    this.auth.login(username, password).subscribe({
      next: () => {
        this.isLoading = false;
        this.messageService.add({severity: 'success', summary: '¡Bienvenido!', detail: 'Inicio de sesión correcto.'});
        // Quitar invite de la URL antes del reload para no reabrir el modal
        this.inviteConsumed.emit();
        setTimeout(() => {
          this.closeModal.emit();
          const cleanUrl = window.location.pathname + window.location.hash;
          window.location.href = cleanUrl || '/';
        }, 1200);
      },
      error: (err) => {
        this.isLoading = false;
        const fallback = err.status === 401
          ? 'Usuario o contraseña incorrectos.'
          : 'Ocurrió un error al iniciar sesión.';
        const msg = err.error?.message || fallback;
        this.messageService.add({severity: 'error', summary: 'Error', detail: msg});
      }
    });
  }

  onRegister() {
    if (this.registerForm.invalid) {
      this.registerForm.markAllAsTouched();
      return;
    }

    this.isLoading = true;
    const raw = this.registerForm.getRawValue();
    const payload = {
      fullname: raw.fullname,
      username: raw.username,
      email: raw.email,
      password: raw.password,
      telephone: raw.telephone,
      birthDate: raw.birthDate,
      inviteToken: raw.inviteToken || undefined
    };

    this.auth.registerUser(payload).subscribe({
      next: () => {
        this.isLoading = false;
        const wasInvite = this.inviteLocked;
        this.messageService.add({
          severity: 'success',
          summary: '¡Cuenta Creada!',
          detail: wasInvite
            ? 'Tu cuenta quedó vinculada. Ya puedes iniciar sesión.'
            : 'Registro exitoso. Por favor inicia sesión.'
        });
        // Importante: limpiar ?invite= ya usado para que un reload/login no reabra el registro
        if (wasInvite) {
          this.inviteConsumed.emit();
          this.inviteLocked = false;
          this.inviteHint = '';
        }
        this.registerForm.reset();
        this.registerForm.get('email')?.enable({ emitEvent: false });
        this.registerForm.get('telephone')?.enable({ emitEvent: false });
        this.registerForm.get('fullname')?.enable({ emitEvent: false });
        setTimeout(() => {
          this.setView('login');
        }, 1200);
      },
      error: (err) => {
        this.isLoading = false;
        const msg = err.error?.message || 'Error inesperado al registrar.';
        this.messageService.add({severity: 'error', summary: 'Error', detail: msg});
      }
    });
  }

  onSendRecoveryEmail() {
    if (this.forgotEmailForm.invalid) {
      this.forgotEmailForm.markAllAsTouched();
      return;
    }

    this.isLoading = true;
    const email = this.forgotEmailForm.value.email;

    this.auth.forgotPassword(email).subscribe({
      next: () => {
        this.isLoading = false;
        this.emailForRecovery = email;
        this.messageService.add({severity: 'success', summary: 'Correo Enviado', detail: 'Revisa tu bandeja de entrada.'});
        this.setView('verify-code');
      },
      error: (err) => {
        this.isLoading = false;
        const msg = err.error?.message || 'Error al enviar código.';
        this.messageService.add({severity: 'error', summary: 'Error', detail: msg});
      }
    });
  }

  onVerifyCode() {
    if (this.verifyCodeForm.invalid) {
      this.verifyCodeForm.markAllAsTouched();
      return;
    }

    this.isLoading = true;
    const code = this.verifyCodeForm.value.code;

    this.auth.verifyRecoveryCode(this.emailForRecovery, code).subscribe({
      next: () => {
        this.isLoading = false;
        this.codeForRecovery = code;
        this.messageService.add({severity: 'success', summary: 'Código Correcto', detail: 'Ingresa tu nueva contraseña.'});
        this.setView('reset-password');
      },
      error: (err) => {
        this.isLoading = false;
        const msg = err.error?.message || 'Código inválido o expirado.';
        this.messageService.add({severity: 'error', summary: 'Error', detail: msg});
      }
    });
  }

  onResetPassword() {
    if (this.resetPasswordForm.invalid) {
      this.resetPasswordForm.markAllAsTouched();
      return;
    }

    const { newPassword, confirmPassword } = this.resetPasswordForm.value;

    if (newPassword !== confirmPassword) {
      this.messageService.add({severity: 'warn', summary: 'Atención', detail: 'Las contraseñas no coinciden.'});
      return;
    }

    this.isLoading = true;

    const data = {
      email: this.emailForRecovery,
      code: this.codeForRecovery,
      newPassword: newPassword
    };

    this.auth.resetPassword(data).subscribe({
      next: () => {
        this.isLoading = false;
        this.messageService.add({severity: 'success', summary: '¡Éxito!', detail: 'Contraseña actualizada.'});
        this.resetPasswordForm.reset();
        setTimeout(() => {
          this.setView('login');
        }, 1500);
      },
      error: (err) => {
        this.isLoading = false;
        const msg = err.error?.message || 'No se pudo cambiar la contraseña.';
        this.messageService.add({severity: 'error', summary: 'Error', detail: msg});
      }
    });
  }

  onResendCode() {
    this.isLoading = true;
    this.auth.forgotPassword(this.emailForRecovery).subscribe({
      next: () => {
        this.isLoading = false;
        this.messageService.add({severity: 'info', summary: 'Reenviado', detail: 'Se ha enviado un nuevo código.'});
      },
      error: () => {
        this.isLoading = false;
        this.messageService.add({severity: 'error', summary: 'Error', detail: 'No se pudo reenviar.'});
      }
    });
  }

  isFieldInvalid(form: FormGroup, field: string): boolean {
    const control = form.get(field);
    return !!(control && control.invalid && (control.dirty || control.touched));
  }
}
